/**
 * Opens Attentive’s sign-up form from a theme button click.
 */
(function () {
  const SELECTOR = '[data-attentive-signup-trigger="true"]';
  const DESKTOP_BREAKPOINT = 760;
  const FALLBACK_MOBILE_CREATIVE_ID = 1360869;
  const FALLBACK_DESKTOP_CREATIVE_ID = 1360875;
  const RETRY_DELAY_MS = 500;
  const MAX_ATTEMPTS = 10;
  const PATCH_WINDOW_MS = 8000;

  const NativeChannel = window.MessageChannel;
  const SWAP_STYLE_ID = 'bonobos-attentive-signup-swap';
  let patchUntil = 0;
  let restoreTimer = 0;
  let revealTimer = 0;

  function hideOverlayDuringSwap() {
    if (document.getElementById(SWAP_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SWAP_STYLE_ID;
    style.textContent =
      '#attentive_overlay,#attentive_creative{' +
      'overflow:hidden!important;opacity:0!important;visibility:hidden!important;' +
      '}';
    document.head.appendChild(style);
  }

  function revealOverlay() {
    if (revealTimer) {
      clearTimeout(revealTimer);
      revealTimer = 0;
    }
    const style = document.getElementById(SWAP_STYLE_ID);
    if (style) style.remove();
  }

  function isSignupFormOpen(data) {
    const payload = data && data.__attentive;
    if (!payload || payload.action !== 'OPEN') return false;
    const pageType = String(payload.pageType || '').toLowerCase();
    return pageType !== '' && pageType !== 'bubble';
  }

  function watchForFormOpen() {
    function onMessage(event) {
      if (!isSignupFormOpen(event.data)) return;
      window.removeEventListener('message', onMessage);
      requestAnimationFrame(revealOverlay);
    }
    window.addEventListener('message', onMessage);
    revealTimer = window.setTimeout(function () {
      window.removeEventListener('message', onMessage);
      revealOverlay();
    }, 1500);
  }

  function patchIsActive() {
    return Date.now() < patchUntil;
  }

  function restoreMessageChannel() {
    patchUntil = 0;
    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = 0;
    }
    if (NativeChannel) window.MessageChannel = NativeChannel;
  }

  /**
   * @param {Record<string, unknown>} config
   */
  function skipBubblePage(config) {
    if (!config || typeof config !== 'object') return;

    if (config.trackingParams && typeof config.trackingParams === 'object') {
      config.trackingParams.firstPageDisplayed = 'FIELD_CAPTURE';
    }
    if (config.creativeParams && typeof config.creativeParams === 'object') {
      config.creativeParams.skippedBubblePage = true;
    }

    let creative = config.creativeConfig;
    if (typeof creative === 'string') {
      try {
        creative = JSON.parse(creative);
        config.creativeConfig = creative;
      } catch (err) {
        return;
      }
    }
    if (!creative || typeof creative !== 'object' || !creative.pages) return;

    const pages = creative.pages;
    const list = Array.isArray(pages) ? pages : Object.values(pages);
    const bubble = list.find(function (page) {
      return page && String(page.type).toLowerCase() === 'bubble';
    });
    const form = list.find(function (page) {
      return page && String(page.type).toLowerCase() === 'field_capture';
    });
    const nextId = (bubble && bubble.nextPage) || (form && form.id);
    if (!nextId) return;

    if (bubble && !Array.isArray(pages) && bubble.id != null) {
      delete pages[bubble.id];
    }
    creative.firstPageId = nextId;
  }

  function patchMessageChannel() {
    if (!NativeChannel || window.MessageChannel.__bonobosSignupPatch) return;

    function wrapPort(port) {
      const post = port.postMessage.bind(port);
      port.postMessage = function (message, transfer) {
        if (patchIsActive() && message && message.__attentiveConfig) {
          skipBubblePage(message.__attentiveConfig);
          restoreMessageChannel();
        }
        return transfer !== undefined ? post(message, transfer) : post(message);
      };
    }

    function PatchedMessageChannel() {
      const channel = new NativeChannel();
      wrapPort(channel.port1);
      return channel;
    }

    PatchedMessageChannel.prototype = NativeChannel.prototype;
    PatchedMessageChannel.__bonobosSignupPatch = true;
    window.MessageChannel = PatchedMessageChannel;
  }

  function getCreativeId(trigger) {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const raw = trigger.getAttribute(
      isDesktop ? 'data-attentive-creative-desktop' : 'data-attentive-creative-mobile'
    );
    const parsed = parseInt(String(raw || '').trim(), 10);
    if (Number.isFinite(parsed)) return parsed;
    return isDesktop ? FALLBACK_DESKTOP_CREATIVE_ID : FALLBACK_MOBILE_CREATIVE_ID;
  }

  function getAttentive() {
    const attentive = window.__attentive;
    return attentive && typeof attentive.trigger === 'function' ? attentive : null;
  }

  /**
   * @param {number} creativeId
   * @param {HTMLElement} button
   * @param {number} attempt
   */
  function openSignup(creativeId, button, attempt) {
    const attentive = getAttentive();
    if (attentive) {
      hideOverlayDuringSwap();
      watchForFormOpen();
      patchUntil = Date.now() + PATCH_WINDOW_MS;
      patchMessageChannel();
      attentive.trigger(null, null, null, creativeId);
      restoreTimer = window.setTimeout(restoreMessageChannel, PATCH_WINDOW_MS);
      return;
    }

    if (attempt < MAX_ATTEMPTS) {
      setTimeout(function () {
        openSignup(creativeId, button, attempt + 1);
      }, RETRY_DELAY_MS);
      return;
    }

    const fallback = button.getAttribute('data-attentive-fallback-href');
    revealOverlay();
    if (fallback) window.location.href = fallback;
  }

  /**
   * @param {MouseEvent} event
   */
  function onClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();
    hideOverlayDuringSwap();

    const creativeId = getCreativeId(button);
    openSignup(creativeId, button, 0);
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(function (button) {
      if (!(button instanceof HTMLElement) || button.dataset.attentiveSignupBound === 'true') return;
      button.dataset.attentiveSignupBound = 'true';
      button.addEventListener('click', onClick);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
